import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { callApi } from 'duck/operations'
import { compact, isEmpty, keyBy, uniq } from 'lodash'
import { all, put, select, takeLatest } from 'redux-saga/effects'
import { deleteDeal as deleteDealFromCache, setDeals, setOneDeal } from 'scenes/Dashboard/data/deals/actions'
import {
  apiDemoteDeal,
  apiGetManagerDeals,
  apiSendDealToInvestor,
  apiUpdateDealFinderStage,
} from 'scenes/Dashboard/data/deals/api'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { fetchAllOrganizations, releaseOrganizationMatches } from 'scenes/Dashboard/data/organizations/actions'
import { apiGetSetOfOrganizationsFromManager } from 'scenes/Dashboard/data/organizations/api'
import { IdIndexedOrganizations } from 'scenes/Dashboard/data/organizations/types'
import { getDealManagerFilters } from 'scenes/Dashboard/scenes/DealManager/duck/selectors'
import { ActionType, getType } from 'typesafe-actions'

import {
  deleteDeal,
  demoteDeal,
  fetchDraftUserGroups,
  fetchDraftUsers,
  fetchManagerPairings,
  releaseDealToInvestor,
  toggleHoldDeal,
  updateCurrentPage,
  updateDealFinderStage,
} from './actions'
import { PairingManagerFilters } from './types'

export function* dealManagerSaga() {
  yield all([
    takeLatest([getType(releaseOrganizationMatches.success), getType(updateCurrentPage)], triggerFetch),
    takeLatest(getType(fetchManagerPairings.request), fetchManagerDealsSaga),
    takeLatest(getType(deleteDeal), deleteDealSaga),
    takeLatest(getType(demoteDeal.request), demoteDealSaga),
    takeLatest(getType(updateDealFinderStage.request), updateStageDealSaga),
    takeLatest(getType(releaseDealToInvestor.request), releaseDealToInvestorSaga),
    takeLatest(getType(releaseDealToInvestor.success), showReleaseDealToInvestorSuccessFlashNotice),
    takeLatest(
      [getType(demoteDeal.failure), getType(updateDealFinderStage.failure), getType(toggleHoldDeal.failure)],
      showErrorUpdatingDeal
    ),
  ])
}

export function* triggerFetch() {
  yield put(fetchManagerPairings.request())
}

export function* fetchManagerDealsSaga() {
  const {
    currentPage,
    organizationSearch,
    corporateSearch,
    corporateStatusFilter,
    scoutingCampaignId,
    isValidFilter,
    finderStagesFilter,
    limit,
  } = (yield select(getDealManagerFilters)) as PairingManagerFilters

  const deals: Deal[] = compact(
    yield callApi(apiGetManagerDeals, currentPage, limit, {
      finderStagesFilter,
      organizationSearch: organizationSearch || undefined,
      corporateSearch: corporateSearch || undefined,
      corporateStatusFilter: corporateStatusFilter || undefined,
      scoutingCampaignId: scoutingCampaignId || undefined,
      isValid: isValidFilter,
    })
  )

  const organizationsIds = uniq(deals.map((deal) => deal.organizationId))
  const organizations: IdIndexedOrganizations = !isEmpty(organizationsIds)
    ? yield callApi(apiGetSetOfOrganizationsFromManager, organizationsIds)
    : {}
  yield put(fetchAllOrganizations.success(organizations))

  yield put(fetchDraftUsers.success({}))
  yield put(fetchDraftUserGroups.success({}))

  yield put(setDeals(keyBy(deals, 'id')))

  const dealsOrder = compact(deals)
  yield put(fetchManagerPairings.success(dealsOrder))
}

function* demoteDealSaga(action: ActionType<typeof demoteDeal.request>) {
  const deal: Deal = yield callApi(apiDemoteDeal, action.payload)
  yield put(demoteDeal.success(deal))
  yield put(setOneDeal(deal))
}

function* updateStageDealSaga(action: ActionType<typeof updateDealFinderStage.request>) {
  const { dealId, newStage } = action.payload
  try {
    const updatedDeal: Deal = yield callApi(apiUpdateDealFinderStage, dealId, newStage)
    yield put(setOneDeal(updatedDeal))
    yield put(updateDealFinderStage.success(updatedDeal))
  } catch (error) {
    yield put(updateDealFinderStage.failure(error))
  }
}

function* showErrorUpdatingDeal() {
  yield put(
    showFlashNotice({
      message: 'There was an error while trying to update deal',
      autoHide: true,
      variant: FlashNoticeVariant.error,
    })
  )
}

function* releaseDealToInvestorSaga(action: ActionType<typeof releaseDealToInvestor.request>) {
  const deal: Deal = yield callApi(apiSendDealToInvestor, action.payload)
  yield put(releaseDealToInvestor.success(deal))
  yield put(setOneDeal(deal))
}

function* showReleaseDealToInvestorSuccessFlashNotice(action: ActionType<typeof releaseDealToInvestor.success>) {
  const deal = action.payload
  yield put(
    showFlashNotice({
      message: `The deal ${deal.id} has been successfully sent to the investor`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* deleteDealSaga(action: ActionType<typeof deleteDeal>) {
  yield put(deleteDealFromCache.request(action.payload.id))
}
